import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Calendar,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  LayoutGrid,
  User,
} from 'lucide-react';
import {
  Button,
  Card,
  ErrorState,
  ExportCsvButton,
  SegmentedControl,
  Select,
  Skeleton,
  toast,
} from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatDate, formatHours } from '../../lib/format';
import { usePeople, useAgreements, useStartTimer, useStopTimer, useTimer, useWeek } from './api';
import { weekCapacity } from './capacity';
import { ApprovalsView } from './ApprovalsView';
import { AttentionStrip, type AttentionTarget } from './AttentionStrip';
import { DayView } from './DayView';
import { WeekApprovalBar } from './WeekApprovalBar';
import { WeekGrid } from './WeekGrid';
import { addDaysIso, mondayOf, todayIso, weekRangeLabel, type HoursFormat } from './hours';
import { exportWeekCsv } from './export-csv';
import { usePersonSelection } from './person';
import { ChangesNotice, ReviewingBanner } from './authorship';
import { dayReview } from './timesheet-status';

/**
 * `approvals` is the approver's QUEUE of periods (days, weeks and months, per
 * each person's cadence) — not a week. It lives in this control because it is
 * where a reviewer already is, but it claims no date: the week stepper and the
 * date jump are hidden while it is open, and the anchor day is kept for when
 * the reader steps back to Week or Day.
 */
type ViewMode = 'week' | 'day' | 'approvals';

const VIEW_KEY = 'financials-time-view';
/** Remembered separately: a week grid needs ~800px, so phones default to Day. */
const VIEW_KEY_MOBILE = 'financials-time-view-mobile';
const HOURS_FORMAT_KEY = 'financials-time-hours-format';

function isNarrow(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
}

function isViewMode(value: string | null): value is ViewMode {
  return value === 'day' || value === 'week' || value === 'approvals';
}

function readStoredView(): ViewMode {
  // Deep link (?view=day&date=YYYY-MM-DD) wins over the remembered view.
  const param = new URLSearchParams(window.location.search).get('view');
  if (isViewMode(param)) return param;
  const narrow = isNarrow();
  try {
    const stored = window.localStorage.getItem(narrow ? VIEW_KEY_MOBILE : VIEW_KEY);
    if (isViewMode(stored)) return stored;
  } catch {
    /* private mode — fall through to the default */
  }
  return narrow ? 'day' : 'week';
}

function readStoredHoursFormat(): HoursFormat {
  try {
    return window.localStorage.getItem(HOURS_FORMAT_KEY) === 'hm' ? 'hm' : 'decimal';
  } catch {
    return 'decimal';
  }
}

function initialDate(): string {
  const param = new URLSearchParams(window.location.search).get('date');
  return param && /^\d{4}-\d{2}-\d{2}$/.test(param) ? param : todayIso();
}

/**
 * Grid-shaped loading state.
 *
 * The old skeleton was a header of eight blocks over five full-width bars — it
 * did not resemble the table that replaced it, so every load ended in a visible
 * layout jump. These are the REAL column widths from `WeekGrid` (label column,
 * seven day columns, total), so the arriving grid lands on top of its own
 * outline.
 */
function GridSkeleton() {
  const label = 'w-[11rem] shrink-0 sm:w-48 xl:w-80';
  const day = 'w-[64px] shrink-0 px-1 xl:w-[80px]';
  const total = 'w-[4.25rem] shrink-0 px-2 sm:w-[5.25rem] sm:px-3';
  const days = Array.from({ length: 7 });
  return (
    <Card aria-busy>
      <div className="overflow-hidden">
        <div className="min-w-[700px]">
          <div className="flex items-center border-b border-grey-200 bg-grey-50 py-2.5">
            <div className={cn(label, 'px-2 sm:px-4')}>
              <Skeleton className="h-3 w-24" />
            </div>
            {days.map((_, i) => (
              <div key={i} className={cn(day, 'flex justify-end')}>
                <Skeleton className="h-3 w-9" />
              </div>
            ))}
            <div className={cn(total, 'flex justify-end')}>
              <Skeleton className="h-3 w-8" />
            </div>
          </div>
          {Array.from({ length: 4 }).map((_, r) => (
            <div key={r} className="flex items-center border-b border-grey-100 py-1">
              <div className={cn(label, 'flex items-center gap-2 px-2 sm:px-4')}>
                <Skeleton className="h-6 w-6 shrink-0 rounded-full sm:h-7 sm:w-7" />
                <div className="min-w-0 flex-1 space-y-1">
                  <Skeleton className="h-3" style={{ width: `${72 - r * 8}%` }} />
                  <Skeleton className="h-2.5 w-1/2" />
                </div>
              </div>
              {days.map((_, i) => (
                <div key={i} className={day}>
                  <Skeleton className="h-8 w-full rounded-md" />
                </div>
              ))}
              <div className={cn(total, 'flex justify-end')}>
                <Skeleton className="h-3 w-10" />
              </div>
            </div>
          ))}
          <div className="flex items-center border-t border-grey-200 bg-grey-50 py-2.5">
            <div className={cn(label, 'px-2 sm:px-4')}>
              <Skeleton className="h-3 w-12" />
            </div>
            {days.map((_, i) => (
              <div key={i} className={cn(day, 'flex flex-col items-end gap-1.5')}>
                <Skeleton className="h-3 w-9" />
                <Skeleton className="h-0.5 w-full" />
              </div>
            ))}
            <div className={cn(total, 'flex justify-end')}>
              <Skeleton className="h-3 w-10" />
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 border-t border-grey-100 px-4 py-2.5">
        <Skeleton className="h-7 w-24 rounded-lg" />
        <Skeleton className="h-7 w-36 rounded-lg" />
        <Skeleton className="ml-auto h-7 w-20 rounded-lg" />
      </div>
    </Card>
  );
}

export function TimePage() {
  const [view, setView] = useState<ViewMode>(readStoredView);
  const [hoursFormat, setHoursFormat] = useState<HoursFormat>(readStoredHoursFormat);
  /**
   * ONE date anchors the screen; the week is derived from it. Keeping a
   * separate `weekStart` meant stepping back three weeks and switching to Day
   * landed you on a day from the week you started in.
   */
  const [selectedDay, setSelectedDay] = useState(initialDate);
  const weekStart = mondayOf(selectedDay);

  const { personId, setPersonId } = usePersonSelection();
  const people = usePeople();

  // Deep link: /time?person=<id> opens someone else's timesheet (the same way
  // ?view=day&date= works), which is how a manager shares a week.
  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get('person');
    if (param) setPersonId(param);
  }, [setPersonId]);

  /**
   * View, date and person live in the URL (the app-wide rule for list state).
   * Switching to Day, stepping a week or reading Tom's sheet used to leave the
   * address bar reading `/time`, so the view was not linkable, bookmarkable or
   * refresh-safe — a manager could not send anyone what they were looking at.
   * `replace` keeps stepping through weeks out of the back-button history.
   */
  const [, setSearchParams] = useSearchParams();
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', view);
        // Always the anchor day, never the derived Monday: a week link still
        // opens the right week (the Monday is computed from it), and a refresh
        // in week view no longer forgets which day you were on when you switch
        // to Day.
        next.set('date', selectedDay);
        // Not written until people load — personId is null on the first render
        // and blanking the param would break a `?person=` deep link.
        if (personId) next.set('person', personId);
        return next;
      },
      { replace: true },
    );
  }, [view, selectedDay, personId, setSearchParams]);

  const agreements = useAgreements();
  const week = useWeek(weekStart, personId);
  const timer = useTimer(personId);
  const startTimer = useStartTimer();
  const stopTimer = useStopTimer();

  useEffect(() => {
    try {
      window.localStorage.setItem(isNarrow() ? VIEW_KEY_MOBILE : VIEW_KEY, view);
    } catch {
      /* fine */
    }
  }, [view]);

  useEffect(() => {
    try {
      window.localStorage.setItem(HOURS_FORMAT_KEY, hoursFormat);
    } catch {
      /* fine */
    }
  }, [hoursFormat]);

  const timerBusy = startTimer.isPending || stopTimer.isPending;
  const runningTimer = timer.data?.timer ?? null;

  const handleStartTimer = useCallback(
    (agreementId: string, taskId: string | null, notes?: string) => {
      startTimer.mutate(
        { personId: personId ?? undefined, agreementId, taskId, notes, date: todayIso() },
        {
          onError: (error) =>
            toast.error(error instanceof Error ? error.message : 'Starting the timer failed.'),
        },
      );
    },
    [personId, startTimer],
  );

  const handleStopTimer = useCallback(() => {
    stopTimer.mutate(
      { personId: personId ?? undefined },
      {
        onSuccess: (result) =>
          // A timer-created entry with no description cannot become an invoice
          // line — say so at the one moment the work is still fresh.
          toast.success(
            result.entry.notes
              ? `Timer stopped — ${formatHours(result.elapsedHours, { unit: true })} added.`
              : `Timer stopped — ${formatHours(result.elapsedHours, {
                  unit: true,
                })} added with no description. Add one from the cell (Alt+Enter).`,
          ),
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : 'Stopping the timer failed.'),
      },
    );
  }, [personId, stopTimer]);

  // Navigation for whichever unit the current view uses. A week step moves the
  // anchor a whole week, so the weekday you were on comes with you. Approvals
  // is a week-shaped view, so it steps by week too.
  const weekly = view !== 'day';
  const goPrev = () => setSelectedDay((d) => addDaysIso(d, weekly ? -7 : -1));
  const goNext = () => setSelectedDay((d) => addDaysIso(d, weekly ? 7 : 1));
  const goToday = () => setSelectedDay(todayIso());
  const onDatePicked = (value: string) => {
    if (!value) return;
    setSelectedDay(value);
  };

  /** Drill from an aggregated week cell into the individual entries of a day. */
  const openDay = useCallback((date: string) => {
    setSelectedDay(date);
    setView('day');
  }, []);

  /**
   * Jump to a week the attention strip named. It moves the SAME anchor day the
   * arrows move, so the URL, the person picker and the stepper all stay in step
   * — the strip is a shortcut through this screen, never a second one.
   */
  const goToAttention = useCallback(
    (periodStart: string, target: AttentionTarget) => {
      // The queue is not dated — opening it must not move the anchor day.
      if (target === 'approvals') {
        setView('approvals');
        return;
      }
      setSelectedDay(periodStart);
      // Your own week, even if an approver was reading somebody else's sheet.
      const own = people.data?.viewer.personId;
      if (own) setPersonId(own);
      if (view === 'approvals') setView('week');
    },
    [people.data, setPersonId, view],
  );

  /**
   * ⚠ WHOSE SHEETS CAN THIS SCREEN OPEN? Exactly the people the picker lists
   * (`/time/people` — ACTIVE people). The person selection accepts no other
   * id: it silently falls back to the viewer. An archived person's submitted
   * period still waits in the Approvals queue on purpose, so opening it used
   * to show the APPROVER'S OWN week under the submitter's name — the one
   * sheet they must not mistake for the one they are about to approve. An
   * unlisted person is therefore not openable, and the queue says so.
   */
  const openableIds = useMemo(
    () => new Set((people.data?.people ?? []).map((p) => p.id)),
    [people.data],
  );
  const canOpenSheet = useCallback((id: string) => openableIds.has(id), [openableIds]);

  /**
   * Open one person's period from the Approvals queue. Same reason as the
   * strip above: view, day and person are STATE seeded from the URL once, so a
   * router navigation to `/time?view=week&…` from inside this page is written
   * straight back over by the URL-sync effect — the queue's links did nothing.
   * The queue keeps real hrefs for new-tab clicks and calls this for a plain one.
   * It REFUSES an unlisted person rather than falling back to anybody else,
   * and never writes that id into the remembered picker selection.
   */
  const openSheet = useCallback(
    (sheetPersonId: string | null, date: string) => {
      if (sheetPersonId && !openableIds.has(sheetPersonId)) {
        toast.error(
          'That person is archived — their timesheet no longer opens here. The period can still be decided from the queue.',
        );
        return;
      }
      const who = sheetPersonId ?? people.data?.viewer.personId ?? null;
      if (who) setPersonId(who);
      setSelectedDay(date);
      setView('week');
    },
    [openableIds, people.data, setPersonId],
  );

  const isCurrentPeriod = weekly ? weekStart === mondayOf(todayIso()) : selectedDay === todayIso();
  const periodLabel = weekly ? weekRangeLabel(weekStart) : formatDate(selectedDay);

  const peopleList = people.data?.people ?? [];
  const activePerson = peopleList.find((p) => p.id === personId);
  const activeCapacity = weekCapacity(activePerson);

  /**
   * Whether this viewer reviews other people's weeks — decided by the SERVER
   * (`/time/people`.viewer), never by reading a role out of the token here.
   * A tab that appears because the frontend guessed, then 403s on click, is
   * worse than no tab.
   */
  const canApprove = people.data?.viewer.canApprove ?? false;
  /**
   * A financial ADMIN may correct a SUBMITTED period (never an approved one) —
   * the server says who that is, and enforces it on every write.
   */
  const isAdmin = people.data?.viewer.isAdmin ?? false;

  /**
   * ⚠ **AM I LOOKING AT SOMEBODY ELSE'S HOURS?**
   *
   * The reviewing screen used to be pixel-identical to your own week — same
   * header, same cells, same "Saved" — so an approver could rewrite a
   * colleague's billable time without ever being told whose it was. It is now
   * a visibly different mode: a banner that says what an edit here means, and
   * a tint on the sheet itself. Nothing is taken away, and it costs no click.
   */
  const viewerPersonId = people.data?.viewer.personId ?? null;
  const foreignSheet = Boolean(
    activePerson && viewerPersonId && activePerson.id !== viewerPersonId,
  );
  /**
   * Foreign writes to the OPEN WEEK, shown on both the week and the day view
   * (the week query runs either way). The owner is told without asking, which
   * is the whole point — a trail behind a route is a trail nobody opens.
   */
  const weekChanges = week.data?.changes ?? [];
  /**
   * Is the sheet on screen closed to THIS viewer? Per day, because a week can
   * sit in two periods (Aug 31 approved, Sep 1–6 open), and an admin may still
   * write into a submitted one.
   */
  const reviewDays = week.data
    ? view === 'day'
      ? [selectedDay]
      : week.data.days
    : [];
  const sheetClosed =
    Boolean(week.data) &&
    reviewDays.every((d) => week.data && dayReview(week.data, d, isAdmin).lock !== null);
  const showApprovals = view === 'approvals' && canApprove;
  // Someone who arrives on `?view=approvals` without the permission (a shared
  // link, a demoted account) lands on their own week rather than on a blank.
  useEffect(() => {
    if (view === 'approvals' && people.data && !canApprove) setView('week');
  }, [view, people.data, canApprove]);

  /**
   * Filled by the week grid with "put the cursor where hours are typed".
   * Falls back to the sheet container so the skip link still lands somewhere
   * useful in the day view (and while the grid is loading).
   */
  const focusSheet = useRef<(() => boolean) | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const skipToSheet = () => {
    if (focusSheet.current?.()) return;
    sheetRef.current?.focus();
  };

  /** The raw server reason, kept OUT of the human-facing explanation. */
  const weekErrorDetail = useMemo(
    () => (week.error instanceof Error ? week.error.message : null),
    [week.error],
  );

  return (
    <div className="relative mx-auto w-full max-w-[1400px]">
      {/* One stop from the top of the page to the cell you type into. The grid
          also takes focus on arrival, so this is the escape hatch for anyone
          who tabbed out of it rather than the only way in. */}
      <button
        type="button"
        onClick={skipToSheet}
        className="sr-only rounded-lg border border-accent bg-white px-3 py-1.5 text-sm font-bold text-accent shadow-elevated focus:not-sr-only focus:absolute focus:left-0 focus:top-0 focus:z-50"
      >
        Skip to timesheet
      </button>

      {/* Header row: title-side context + controls. Kept on ONE line with the
          controls — putting the person line beside the date instead of under
          it pushed the control strip onto a second row and cost more height
          than it saved. */}
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0">
          {/* Approvals is a QUEUE of periods (days, weeks, months), not a week —
              so it claims no date range and offers no week stepper. */}
          <h2 className="truncate text-heading-3 leading-tight text-secondary">
            {showApprovals ? 'Approvals' : periodLabel}
          </h2>
          {showApprovals ? (
            <p className="flex flex-wrap items-center gap-x-1.5 text-caption leading-tight text-grey-500">
              <ClipboardCheck className="h-3 w-3 shrink-0" aria-hidden />
              Timesheets waiting for review, and those not yet submitted
            </p>
          ) : (
            activePerson && (
              <p className="flex flex-wrap items-center gap-x-1.5 text-caption leading-tight text-grey-500">
                <User className="h-3 w-3 shrink-0" aria-hidden />
                Timesheet for {activePerson.name}
                {activePerson.type === 'CONTRACTOR' && <span>· contractor</span>}
                {/* The week's target in words, so the big "of 16h" below is
                    never a number without a source. */}
                <span>
                  ·{' '}
                  {activeCapacity.weekly === null ? (
                    'no weekly target'
                  ) : (
                    <>
                      contracted <span className="num">{activeCapacity.weeklyLabel}</span>/week
                    </>
                  )}
                </span>
              </p>
            )
          )}
        </div>

        {/* On a phone these used to be three stacked control rows — a whole
            screen of chrome before one hour of data. The view toggle and the
            period stepper now share a line, and the person picker takes the
            full width above them instead of forcing a third row. */}
        <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
          {/* Reserved in the skeleton too — without it the whole control strip
              jumps sideways the moment people load. */}
          {/* Approvals is about EVERYONE, so a "whose sheet?" picker there is
              a control with nothing to control. */}
          {showApprovals ? null : people.isPending ? (
            <Skeleton className="h-9 w-full min-w-0 basis-full rounded-lg sm:w-52 sm:basis-auto" />
          ) : peopleList.length > 1 ? (
            <Select
              aria-label="Person"
              selectSize="sm"
              // ⚠ IT NEEDS A ROW OF ITS OWN ON A PHONE. Sharing the line
              // with the three-option view strip left it about 100px wide, so
              // "Jesse McCabe" rendered as "Jes" — a picker that cannot name
              // the person it has picked. Only approvers ever see it (a member
              // gets one name and no control), so the extra row costs nothing
              // for everybody else.
              className="w-full min-w-0 basis-full sm:w-52 sm:basis-auto sm:flex-none"
              value={personId ?? ''}
              onChange={(e) => setPersonId(e.target.value)}
              options={peopleList.map((person) => ({
                value: person.id,
                label: person.type === 'CONTRACTOR' ? `${person.name} (contractor)` : person.name,
              }))}
            />
          ) : null}

          <SegmentedControl
            ariaLabel="Timesheet view"
            value={view}
            onChange={setView}
            className="h-9"
            options={[
              {
                value: 'week',
                label: (
                  <span className="flex items-center gap-1.5 px-1 py-0.5 text-sm">
                    <LayoutGrid className="h-3.5 w-3.5" aria-hidden /> Week
                  </span>
                ),
              },
              {
                value: 'day',
                label: (
                  <span className="flex items-center gap-1.5 px-1 py-0.5 text-sm">
                    <CalendarDays className="h-3.5 w-3.5" aria-hidden /> Day
                  </span>
                ),
              },
              // The manager's roster is the same week seen from above, so it
              // belongs in the view control rather than on a route of its own.
              // Offered only to approvers — the server decides that.
              ...(canApprove
                ? [
                    {
                      value: 'approvals' as const,
                      label: (
                        <span className="flex items-center gap-1.5 px-1 py-0.5 text-sm">
                          <ClipboardCheck className="h-3.5 w-3.5" aria-hidden /> Approvals
                        </span>
                      ),
                    },
                  ]
                : []),
            ]}
          />

          {/* Period navigation. On a phone it takes a full row and spreads
              across it: `ml-auto` used to shove the cluster right and leave a
              ragged empty gutter beside it, which read as a layout bug. */}
          {!showApprovals && (
          <div className="flex w-full items-center justify-between gap-1 sm:ml-0 sm:w-auto sm:justify-start">
            <button
              type="button"
              onClick={goPrev}
              aria-label={weekly ? 'Previous week' : 'Previous day'}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-grey-200 bg-white text-grey-500 transition-colors hover:bg-grey-50 hover:text-secondary"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </button>
            <Button variant="outline" size="sm" onClick={goToday} disabled={isCurrentPeriod} className="h-9">
              Today
            </Button>
            <button
              type="button"
              onClick={goNext}
              aria-label={weekly ? 'Next week' : 'Next day'}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-grey-200 bg-white text-grey-500 transition-colors hover:bg-grey-50 hover:text-secondary"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
            <label className="relative ml-1 flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-grey-200 bg-white text-grey-500 transition-colors hover:bg-grey-50 hover:text-secondary">
              <Calendar className="h-4 w-4" aria-hidden />
              <span className="sr-only">Jump to date</span>
              <input
                type="date"
                value={weekly ? weekStart : selectedDay}
                onChange={(e) => onDatePicked(e.target.value)}
                className="absolute inset-0 cursor-pointer opacity-0"
                tabIndex={-1}
                aria-label="Jump to date"
              />
            </label>
            {/* /time was the last data screen with no export — the Friday
                spreadsheet Harvest users have always had. Approvals exports its
                own roster from inside its filter bar. */}
            <ExportCsvButton
              className="ml-1 h-9"
              count={week.data?.rows.reduce((n, r) => n + r.entries.length, 0) ?? 0}
              noun="time entries"
              scope="in this week"
              onClick={() => week.data && exportWeekCsv(week.data)}
            />
          </div>
          )}
        </div>
      </div>

      {/* What needs this viewer BEYOND what the status bar below accounts for
          — the week, or in Day view the one day. Renders nothing at all when
          there is nothing outstanding, and drops anything that bar already
          shows. */}
      <AttentionStrip
        displayedStart={view === 'day' ? selectedDay : weekStart}
        displayedEnd={view === 'day' ? selectedDay : addDaysIso(weekStart, 6)}
        view={showApprovals ? 'approvals' : view === 'day' ? 'day' : 'week'}
        viewingOwnSheet={!foreignSheet}
        hoursFormat={hoursFormat}
        onGoTo={goToAttention}
      />

      <div id="timesheet" ref={sheetRef} tabIndex={-1} className="outline-none">
        {!showApprovals && foreignSheet && activePerson && (
          <ReviewingBanner
            ownerName={activePerson.name}
            locked={sheetClosed}
          />
        )}
        {!showApprovals && (
          <ChangesNotice
            changes={weekChanges}
            ownerName={activePerson?.name ?? 'this person'}
            isOwnWeek={!foreignSheet}
          />
        )}
        {showApprovals ? (
          <ApprovalsView
            hoursFormat={hoursFormat}
            onOpenSheet={openSheet}
            canOpenSheet={canOpenSheet}
          />
        ) : view === 'week' ? (
          week.isPending || agreements.isPending ? (
            <>
              <Card className="mb-3 grid grid-cols-1 divide-grey-100 sm:grid-cols-3 sm:divide-x" aria-busy>
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="space-y-2 px-4 py-2.5">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-6 w-28" />
                    <Skeleton className="h-1.5 w-full" />
                  </div>
                ))}
              </Card>
              <GridSkeleton />
            </>
          ) : week.isError ? (
            // The server's own words are diagnostic, not an explanation — a
            // one-word 500 body ("boom") used to be printed to the user as the
            // reason their timesheet was missing.
            <Card>
              <ErrorState
                title="Couldn't load your timesheet"
                message="Your saved hours are safe — this is the read that failed, not a write. Try again, and if it keeps failing let an admin know."
                onRetry={() => week.refetch()}
              />
              {weekErrorDetail && (
                <p className="border-t border-grey-100 px-6 py-2 text-center text-caption text-grey-400">
                  Technical detail:{' '}
                  <span className="font-mono text-grey-500">{weekErrorDetail}</span>
                </p>
              )}
            </Card>
          ) : (
            <>
              {/* The week header's submission strip: the status pill, who
                  reviewed it and when, the reviewer's note, and the Friday
                  action. Above the summary because "is this week done?" is
                  answered before "how many hours is it?". */}
              <WeekApprovalBar
                range={week.data}
                dayTotals={week.data.dayTotals}
                personId={week.data.person.id}
                week={week.data}
                isAdmin={isAdmin}
                hoursFormat={hoursFormat}
                runningTimer={runningTimer}
              />
              <WeekGrid
                key={`${personId ?? 'default'}:${weekStart}`}
                week={week.data}
                weekStart={weekStart}
                personId={personId}
                agreements={agreements.data?.agreements ?? []}
                runningTimer={runningTimer}
                timerBusy={timerBusy}
                hoursFormat={hoursFormat}
                onHoursFormatChange={setHoursFormat}
                onStartTimer={handleStartTimer}
                onStopTimer={handleStopTimer}
                onOpenDay={openDay}
                focusRef={focusSheet}
                foreignSheet={foreignSheet}
                isAdmin={isAdmin}
              />
            </>
          )
        ) : (
          <>
          {/* The day's own period, with its Submit — on a phone the day view
              IS the timesheet, and a monthly contractor has to be able to send
              the month in from it. */}
          {week.data && (
            <WeekApprovalBar
              range={week.data}
              dayTotals={week.data.dayTotals}
              personId={week.data.person.id}
              focusDate={selectedDay}
              isAdmin={isAdmin}
              hoursFormat={hoursFormat}
              runningTimer={runningTimer}
            />
          )}
          <DayView
            date={selectedDay}
            personId={personId}
            agreements={agreements.data?.agreements ?? []}
            runningTimer={runningTimer}
            timerBusy={timerBusy}
            hoursFormat={hoursFormat}
            onHoursFormatChange={setHoursFormat}
            onStartTimer={handleStartTimer}
            onStopTimer={handleStopTimer}
            onSelectDay={onDatePicked}
            foreignSheet={foreignSheet}
            isAdmin={isAdmin}
          />
          </>
        )}
      </div>
    </div>
  );
}
